import Coin from '../../Components/Coin';
import EmoteIcon from '../../Components/EmoteIcon';
import Gem from '../../Components/Gem';
import ProfileAvatar from '../../Components/ProfileAvatar';
import { getPlayerSkinDef, skinSwatchBackground } from '../../player-colors';
import { formatCurrencyAmount } from '../../utils/format-currency';

export type SkinRarity = 'common' | 'uncommon' | 'rare' | 'legendary';

export type StoreItem = {
  itemId?: string;
  title?: string;
  subTitle?: string;
  description?: string;
  comingSoon?: boolean;
  imageURL?: string;
  backgroundColor?: string;
  category?: string;
  subCategory?: string;
  skinId?: string;
  colorId?: string;
  iconId?: string;
  emoteId?: string;
  fill?: string;
  stroke?: string;
  rarity?: SkinRarity | string;
  money?: number;
  moneyDiscount?: number;
  moneyDiscountExpiration?: number;
  priceCoins?: number;
  priceGems?: number;
  grantCoins?: number;
  grantGems?: number;
  coins?: number;
  coinsDiscount?: number;
  coinsDiscountExpiration?: number;
  gems?: number;
  gemsDiscount?: number;
  gemsDiscountExpiration?: number;
  duration?: number;
  durationType?: string;
  legacySeasonId?: string;
  /** Client-only: exclusive to battle pass (not purchasable here) */
  passExclusive?: boolean;
};

export type StoreItemTileProps = {
  item: StoreItem;
  badge?: string | null;
  onClick?: (item: StoreItem) => void;
};

const RARITY_STYLES: Record<string, string> = {
  common: 'border-bk-border text-bk-muted bg-bk-bg/70',
  uncommon: 'border-emerald-500/50 text-emerald-300 bg-emerald-950/50',
  rare: 'border-sky-500/50 text-sky-300 bg-sky-950/50',
  legendary: 'border-bk-gold text-bk-gold-bright bg-bk-gold/10',
};

export function rarityClass(rarity?: string) {
  return RARITY_STYLES[String(rarity ?? '').toLowerCase()] ?? RARITY_STYLES.common;
}

export default function StoreItemTile({
  item,
  item: {
    title,
    subTitle,
    comingSoon,
    imageURL,
    backgroundColor = '#151c2e',
    skinId,
    colorId,
    iconId,
    emoteId,
    money,
    coins,
    gems,
    priceCoins,
    priceGems,
    grantGems,
    rarity,
    category,
    passExclusive,
  },
  badge,
  onClick = () => {},
}: StoreItemTileProps) {
  const resolvedSkinId = skinId ?? colorId;
  const isSkin = category === 'skins' || category === 'colors';
  const isIcon = category === 'icons';
  const isEmote = category === 'emotes';
  const swatch = resolvedSkinId
    ? skinSwatchBackground(getPlayerSkinDef(String(resolvedSkinId)))
    : backgroundColor;
  const hasImage = Boolean(imageURL);
  const displayCoins = Number(priceCoins ?? coins ?? 0);
  const displayGems = Number(priceGems ?? gems ?? 0);
  const displayMoney = Number(money ?? 0);
  const packGems = Number(grantGems ?? 0);
  const locked = comingSoon || passExclusive;

  return (
    <button
      type="button"
      className="group relative flex min-h-44 flex-col overflow-hidden border border-bk-border bg-bk-panel text-left transition-colors hover:border-bk-gold/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-bk-gold"
      onClick={() => onClick(item)}
    >
      <div
        className="relative h-28 w-full"
        style={
          hasImage
            ? {
                backgroundColor: '#151c2e',
                backgroundImage: `url(${imageURL})`,
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }
            : { background: isSkin ? swatch : `radial-gradient(circle at 35% 30%, ${backgroundColor}88, #0a0e17 75%)` }
        }
      >
        {!hasImage && isSkin ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div
              className="h-14 w-14 rounded-full border-4 border-black/35 shadow-lg"
              style={{ background: swatch }}
            />
          </div>
        ) : null}
        {!hasImage && isIcon ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <ProfileAvatar avatarId={String(iconId)} size="md" />
          </div>
        ) : null}
        {!hasImage && isEmote ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <EmoteIcon emoteId={String(emoteId)} size="lg" />
          </div>
        ) : null}
        {!hasImage && category === 'gems' ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="scale-[2.2]">
              <Gem />
            </div>
          </div>
        ) : null}
        {locked ? (
          <div className="absolute inset-0 flex items-center justify-center bg-bk-bg/55 backdrop-blur-[1px]">
            <span className="border border-bk-gold bg-bk-bg/80 px-3 py-1 text-xs font-bold uppercase tracking-wider text-bk-gold">
              {passExclusive ? 'Battle Pass' : 'Coming Soon'}
            </span>
          </div>
        ) : null}
        {badge ? (
          <span className="absolute left-2 top-2 bg-bk-bg/85 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-bk-gold">
            {badge}
          </span>
        ) : null}
        {rarity ? (
          <span
            className={`absolute right-2 top-2 border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${rarityClass(String(rarity))}`}
          >
            {rarity}
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-1 px-3 py-2.5">
        <div className="font-semibold text-bk-text">{title}</div>
        {subTitle ? (
          <div className="text-xs text-bk-muted">{subTitle}</div>
        ) : null}
        <div className="mt-auto flex flex-wrap items-center gap-2 pt-1">
          {passExclusive ? (
            <span className="text-xs font-semibold uppercase tracking-wide text-bk-gold">
              Season exclusive
            </span>
          ) : null}
          {!passExclusive && displayMoney > 0 ? (
            <span className="bg-bk-gold px-2 py-0.5 text-sm font-semibold text-bk-bg">
              {displayMoney.toLocaleString('en-US', {
                style: 'currency',
                currency: 'USD',
              })}
            </span>
          ) : null}
          {!passExclusive && displayCoins > 0 ? (
            <span className="flex items-center gap-1 text-sm text-bk-gold tabular-nums">
              <Coin /> {formatCurrencyAmount(displayCoins)}
            </span>
          ) : null}
          {!passExclusive && displayGems > 0 ? (
            <span className="flex items-center gap-1 text-sm text-fuchsia-300 tabular-nums">
              <Gem /> {formatCurrencyAmount(displayGems)}
            </span>
          ) : null}
          {!passExclusive && displayMoney > 0 && packGems > 0 ? (
            <span className="text-xs text-bk-muted tabular-nums">
              {formatCurrencyAmount(packGems)} gems
            </span>
          ) : null}
        </div>
      </div>
    </button>
  );
}
